import { useState } from "react";
import { useLanguage } from "@/lib/language-context";
import { useApp } from "@/lib/app-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronUp, HelpCircle, Phone, MessageCircle } from "lucide-react";

type FAQItem = {
  question: string;
  answer: string;
};

export function FAQScreen() {
  const { t } = useLanguage();
  const { setCurrentScreen } = useApp();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    { question: t.faq1Q, answer: t.faq1A },
    { question: t.faq2Q, answer: t.faq2A },
    { question: t.faq3Q, answer: t.faq3A },
    { question: t.faq4Q, answer: t.faq4A },
    { question: t.faq5Q, answer: t.faq5A },
    { question: t.faq6Q, answer: t.faq6A },
    { question: t.faq7Q, answer: t.faq7A },
    { question: t.faq8Q, answer: t.faq8A },
    { question: t.faq9Q, answer: t.faq9A },
    { question: t.faq10Q, answer: t.faq10A },
    { question: t.faq11Q, answer: t.faq11A },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      {/* Main Content */}
      <div className="flex-1 px-4 py-8">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">{t.faqTitle}</h1>
            <p className="text-muted-foreground">{t.faqSubtitle}</p>
          </div>

          {/* FAQ List */}
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <Card
                key={index}
                className={`overflow-hidden transition-all ${
                  openIndex === index ? "border-primary" : ""
                }`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-5 text-left flex items-start gap-3"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary text-primary font-bold flex items-center justify-center text-sm">
                    {index + 1}
                  </span>
                  <span className="flex-1 text-lg font-medium text-foreground leading-snug">
                    {faq.question}
                  </span>
                  <span className="flex-shrink-0 text-primary mt-1">
                    {openIndex === index ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </span>
                </button>
                
                {openIndex === index && (
                  <div
                    id={`faq-answer-${index}`}
                    className="px-5 pb-5 pt-0"
                  >
                    <div className="pl-11">
                      <p className="text-base text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Contact Section */}
          <Card className="p-6 mt-8 bg-primary text-primary-foreground border-0">
            <h3 className="text-xl font-bold mb-4 text-center">{t.contactTitle}</h3>
            <p className="text-center mb-6 opacity-90">{t.contactDesc}</p>
            
            <div className="flex flex-col gap-3">
              {/* Call Button */}
              <a
                href={`tel:+${t.phoneNumberRaw}`}
                className="flex items-center justify-center gap-3 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
              >
                <Phone className="h-6 w-6" />
                <span className="font-bold text-lg">{t.callUs}: {t.phoneNumber}</span>
              </a>
              
              {/* WhatsApp Button */}
              <a
                href={`https://wa.me/${t.phoneNumberRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 p-4 bg-[#25D366] rounded-xl hover:opacity-90 transition-opacity"
              >
                <MessageCircle className="h-6 w-6" />
                <span className="font-bold text-lg">{t.whatsappUs}</span>
              </a>
            </div>

            <p className="text-center text-sm mt-4 opacity-75">{t.officeHours}</p>
          </Card>
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="sticky bottom-0 p-4 bg-card border-t border-border safe-area-inset-bottom">
        <div className="max-w-lg mx-auto flex gap-3">
          <Button
            onClick={() => setCurrentScreen("home")}
            variant="outline"
            size="lg"
            className="flex-1 h-14 text-lg font-bold rounded-xl border-2 bg-transparent"
          >
            {t.goHome}
          </Button>
          <Button
            onClick={() => setCurrentScreen("apply-country")}
            size="lg"
            className="flex-1 h-14 text-lg font-bold rounded-xl"
          >
            {t.apply}
          </Button>
        </div>
      </div>
    </div>
  );
}
